import { Directive, ElementRef, AfterViewInit, OnDestroy, Renderer2, inject, OnChanges, SimpleChanges, TemplateRef, ViewContainerRef, EmbeddedViewRef, input } from '@angular/core';

/**
 * ElementWrapperDirective
 * 
 * A generic directive that finds specified elements within a component and wraps 
 * their content with either a simple HTML element or a custom Angular template.
 * Supports dynamic content detection and conditional wrapping.
 * 
 * @example
 * // Basic HTML wrapper
 * <adp-input 
 *   wrapElements="mat-hint"
 *   wrapTag="a"
 *   [wrapAttributes]="{ href: 'https://help.com', target: '_blank' }"
 *   wrapClass="help-link">
 * </adp-input>
 * 
 * @example
 * // Template wrapper with conditional enabling
 * <adp-input 
 *   wrapElements="mat-hint"
 *   [wrapTemplate]="helpTemplate"
 *   [wrapEnabled]="showHelp">
 * </adp-input>
 * <ng-template #helpTemplate let-content>
 *   <a (click)="openHelp()" class="help-link">
 *     <mat-icon>help</mat-icon>{{ content }}
 *   </a>
 * </ng-template>
 */
@Directive({
  selector: '[wrapElements]',
  standalone: true,
})
export class ElementWrapperDirective implements AfterViewInit, OnDestroy, OnChanges {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private viewContainer = inject(ViewContainerRef);
  private mutationObserver?: MutationObserver;
  private processedElements = new WeakSet<Element>();
  private templateViews: EmbeddedViewRef<any>[] = [];
  private initialScanTimeoutId?: ReturnType<typeof setTimeout>;

  // Bounded retry/poll delays (ms) for the initial post-view-init scan, spanning ~500ms
  // worst case. Some targets (e.g. a <mat-label> inside a lazily instantiated mat-tab)
  // aren't in the DOM yet at a single fixed 100ms check because Angular Material's own
  // notched-outline label measurement is an async layout pass with variable timing.
  // Retrying a handful of times, bailing out early once nothing is left unwrapped, covers
  // that race without resorting to an unbounded loop. Kept tight (vs. a longer spread)
  // because autofocus is enabled on these same fields, so a keyboard/AT user tabbing
  // through early is plausible -- the sooner the wrap can land, the less likely they tab
  // past it. The MutationObserver below remains as-is for elements added well after this
  // initial window (e.g. dynamic form changes).
  private static readonly INITIAL_SCAN_RETRY_DELAYS_MS = [50, 75, 100, 125, 150];

  readonly wrapElements = input<string>(''); // CSS selector for elements to wrap (required)
  readonly wrapTag = input<string>('span'); // HTML tag for wrapper element
  readonly wrapAttributes = input<{
    [key: string]: string;
}>({}); // Attributes for wrapper
  readonly wrapClass = input<string>(''); // CSS class for wrapper
  readonly wrapTemplate = input<TemplateRef<any>>(undefined); // Custom template for wrapper
  readonly preserveOriginal = input<boolean>(false); // Keep original element structure
  readonly wrapContent = input<boolean>(true); // Wrap content (true) or whole element (false)
  readonly wrapEnabled = input<boolean>(true); // Enable/disable wrapping based on condition

  ngOnChanges(changes: SimpleChanges): void {
    const relevantChanges = ['wrapElements', 'wrapTag', 'wrapAttributes', 'wrapClass', 'wrapTemplate', 'wrapEnabled'];
    const hasRelevantChanges = relevantChanges.some(key => changes[key] && !changes[key].firstChange);
    
    if (hasRelevantChanges) {
      // Cancel any in-flight initial-scan retry chain before swapping WeakSets -- otherwise
      // a stale outstanding tick can see the already-wrapped, possibly-focused element as
      // "unprocessed" against the new WeakSet and re-wrap it (destroying live focus).
      if (this.initialScanTimeoutId) {
        clearTimeout(this.initialScanTimeoutId);
      }
      this.processedElements = new WeakSet();
      this.scheduleInitialScan(0);
    }
  }

  ngAfterViewInit(): void {
    // Initial processing with a bounded retry/poll to allow child components to render,
    // including targets whose availability depends on Angular Material's own async
    // layout timing (see INITIAL_SCAN_RETRY_DELAYS_MS above).
    this.scheduleInitialScan(0);

    // Set up mutation observer to handle dynamically added elements
    this.setupMutationObserver();
  }

  ngOnDestroy(): void {
    if (this.initialScanTimeoutId) {
      clearTimeout(this.initialScanTimeoutId);
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Clean up template views
    this.templateViews.forEach(view => {
      if (!view.destroyed) {
        view.destroy();
      }
    });
    this.templateViews = [];
  }

  /**
   * Bounded retry/poll for the initial scan: attempts a wrap pass, then reschedules
   * itself only if unwrapped targets remain and attempts are left in
   * INITIAL_SCAN_RETRY_DELAYS_MS. Stops as soon as nothing is left to wrap, or once
   * the bound is exhausted -- never an unbounded loop.
   */
  private scheduleInitialScan(attempt: number): void {
    const delay = ElementWrapperDirective.INITIAL_SCAN_RETRY_DELAYS_MS[attempt];
    if (delay === undefined) {
      return;
    }

    this.initialScanTimeoutId = setTimeout(() => {
      this.processElements();

      if (this.hasUnprocessedTargets()) {
        this.scheduleInitialScan(attempt + 1);
      }
    }, delay);
  }

  /**
   * Shared guard for both processElements() and hasUnprocessedTargets(): wrapping is only
   * applicable when a selector is configured and wrapping is currently enabled.
   */
  private canProcess(): boolean {
    return !!this.wrapElements() && this.wrapEnabled();
  }

  private hasUnprocessedTargets(): boolean {
    if (!this.canProcess()) {
      return false;
    }

    const targetElements: NodeListOf<Element> = this.elementRef.nativeElement.querySelectorAll(this.wrapElements());
    if (targetElements.length === 0) {
      // Nothing matched yet -- this does NOT mean "fully processed". The target likely
      // hasn't rendered into the DOM yet (e.g. a lazily-instantiated mat-tab), which is
      // exactly the case this retry chain exists to handle. Keep retrying until the
      // bounded attempt budget is exhausted.
      return true;
    }

    return Array.from(targetElements).some(element => !this.processedElements.has(element));
  }

  private setupMutationObserver(): void {
    if (!window.MutationObserver || !this.wrapElements()) {
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      const shouldProcess = mutations.some(mutation => 
        mutation.type === 'childList' && 
        mutation.addedNodes.length > 0 &&
        Array.from(mutation.addedNodes).some(node => {
        const wrapElements = this.wrapElements();
        return node.nodeType === Node.ELEMENT_NODE && 
          ((node as Element).matches?.(wrapElements) || 
           (node as Element).querySelector?.(wrapElements));
      }
        )
      );

      if (shouldProcess) {
        setTimeout(() => this.processElements(), 50);
      }
    });

    this.mutationObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  private processElements(): void {
    if (!this.canProcess()) {
      return;
    }

    const targetElements = this.elementRef.nativeElement.querySelectorAll(this.wrapElements());
    
    targetElements.forEach((element: HTMLElement) => {
      // Skip if already processed
      if (this.processedElements.has(element)) {
        return;
      }

      this.processedElements.add(element);
      
      // Check if element has content or if we should wait for it
      const content = this.getElementContent(element);
      
      if (!content.trim()) {
        // Element is empty, set up watcher for future content
        this.setupContentWatcher(element);
      }
      
      // Process element regardless of current content state
      this.wrapElement(element);
    });
  }

  private wrapElement(element: HTMLElement): void {
    if (this.wrapTemplate()) {
      this.wrapWithTemplate(element);
    } else {
      this.wrapWithHtmlElement(element);
    }
  }

  private wrapWithHtmlElement(element: HTMLElement): void {
    if (this.wrapContent()) {
      // Wrap content inside the element
      if (element.children.length > 0 || element.textContent?.trim()) {
        // Check if already wrapped
        if (element.children.length === 1 && 
            element.children[0].tagName.toLowerCase() === this.wrapTag().toLowerCase()) {
          return;
        }

        const wrapper = this.renderer.createElement(this.wrapTag());
        this.applyWrapperAttributes(wrapper);
        
        // Move all content to wrapper
        while (element.firstChild) {
          this.renderer.appendChild(wrapper, element.firstChild);
        }
        
        this.renderer.appendChild(element, wrapper);
      }
    } else {
      // Wrap the entire element
      const wrapTag = this.wrapTag();
      if (element.parentElement?.tagName.toLowerCase() === wrapTag.toLowerCase()) {
        return;
      }

      const wrapper = this.renderer.createElement(wrapTag);
      this.applyWrapperAttributes(wrapper);
      
      // Insert wrapper before element
      this.renderer.insertBefore(element.parentElement, wrapper, element);
      // Move element into wrapper
      this.renderer.appendChild(wrapper, element);
    }
  }

  private wrapWithTemplate(element: HTMLElement): void {
    const wrapTemplate = this.wrapTemplate();
    if (!wrapTemplate) {
      return;
    }

    const content = this.wrapContent() ? this.getElementContent(element) : element.outerHTML;
    
    // Create embedded view from template but don't use ViewContainerRef insertion
    const embeddedView: EmbeddedViewRef<any> = wrapTemplate.createEmbeddedView({
      $implicit: content,
      content: content,
      element: element
    });

    // Detect changes to ensure template is fully rendered
    embeddedView.detectChanges();

    if (this.wrapContent()) {
      // Clear the original content
      this.renderer.setProperty(element, 'innerHTML', '');
      
      // Directly append the template's DOM nodes to the target element
      embeddedView.rootNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
          this.renderer.appendChild(element, node);
        }
      });
    } else {
      // Replace entire element with template
      const parent = element.parentElement;
      if (parent) {
        // Insert template nodes before the original element
        embeddedView.rootNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
            this.renderer.insertBefore(parent, node, element);
          }
        });
        
        if (!this.preserveOriginal()) {
          this.renderer.removeChild(parent, element);
        }
      }
    }

    // Keep the view reference to prevent garbage collection
    this.templateViews.push(embeddedView);
  }

  private setupContentWatcher(element: HTMLElement): void {
    const contentObserver = new MutationObserver((mutations) => {
      const hasContent = mutations.some(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          return this.getElementContent(element).trim().length > 0;
        }
        return false;
      });
      
      if (hasContent) {
        contentObserver.disconnect();
        this.processedElements.delete(element);
        setTimeout(() => this.wrapElement(element), 50);
      }
    });

    contentObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Fallback timer check for content
    let checkCount = 0;
    const maxChecks = 20; // 10 seconds maximum
    
    const contentChecker = setInterval(() => {
      checkCount++;
      
      if (checkCount > maxChecks) {
        clearInterval(contentChecker);
        contentObserver.disconnect();
        return;
      }
      
      if (this.getElementContent(element).trim()) {
        clearInterval(contentChecker);
        contentObserver.disconnect();
        this.processedElements.delete(element);
        setTimeout(() => this.wrapElement(element), 100);
      }
    }, 500);
  }

  private getElementContent(element: HTMLElement): string {
    const content = element.innerHTML || element.textContent || '';
    return content.replace(/<!--[\s\S]*?-->/g, '').trim();
  }

  private applyWrapperAttributes(wrapper: HTMLElement): void {
    // Apply CSS class
    const wrapClass = this.wrapClass();
    if (wrapClass) {
      this.renderer.setAttribute(wrapper, 'class', wrapClass);
    }

    // Apply custom attributes
    Object.keys(this.wrapAttributes()).forEach(key => {
      this.renderer.setAttribute(wrapper, key, this.wrapAttributes()[key]);
    });
  }
}